function padTimeUnit(value) { 
  return String(value).padStart(2, "0");
}

function toArrayLike(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  try {
    return Array.from(value);
  } catch (_error) {
    if (typeof value.length === "number") {
      return Array.prototype.slice.call(value);
    }
  }

  return [];
}

export function getLegDurationSeconds(leg) {
  const rawValue =
    leg?.duration?.value ??
    leg?.duration?.seconds ??
    leg?.durationValue;
  const value = Number(rawValue);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }
  return value;
}

export function getLegDistanceMeters(leg) {
  const value = Number(leg?.distance?.value);
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }
  return value;
}

export function secondsToScheduleMinutes(seconds) {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }

  return Math.max(1, Math.round(value / 60));
}

export function addMinutesToHHmm(timeHHmm, minutesToAdd) {
  if (!/^\d{2}:\d{2}$/.test(timeHHmm)) {
    return "";
  }

  const [hours, minutes] = timeHHmm.split(":").map(Number);
  const totalMinutes =
    ((hours * 60 + minutes + Number(minutesToAdd)) % 1440 + 1440) % 1440;

  return `${padTimeUnit(Math.floor(totalMinutes / 60))}:${padTimeUnit(
    totalMinutes % 60,
  )}`;
}

export function extractDirectionsLegs(directionsLike) {
  const payload = directionsLike?.directions || directionsLike;
  const routes = toArrayLike(payload?.routes);
  return toArrayLike(routes[0]?.legs);
}

export function getSegmentDurationsFromStopoverLegs(legs) {
  const normalizedLegs = toArrayLike(legs);
  if (normalizedLegs.length === 0) {
    return [];
  }

  return normalizedLegs.map((leg) => getLegDurationSeconds(leg));
}

export function snapshotDirectionsForSchedule(directionsLike) {
  if (!directionsLike) {
    return null;
  }

  return {
    routes: [
      {
        legs: extractDirectionsLegs(directionsLike).map((leg) => ({
          duration: { value: getLegDurationSeconds(leg) },
          distance: { value: getLegDistanceMeters(leg) },
        })),
      },
    ],
  };
}

export function getTravelDurationsFromDirections(directionsLike) {
  const durations = getSegmentDurationsFromStopoverLegs(
    extractDirectionsLegs(directionsLike),
  );

  if (!durations.length || durations.some((duration) => duration == null)) {
    return [];
  }

  return durations;
}

export function buildStopToStopSegments({
  coordinateStopIds = [],
  legs = [],
} = {}) {
  const stopIndexes = [];

  for (let index = 0; index < coordinateStopIds.length; index += 1) {
    if (coordinateStopIds[index] != null) {
      stopIndexes.push(index);
    }
  }

  const segments = [];

  for (let stopOffset = 0; stopOffset < stopIndexes.length - 1; stopOffset += 1) {
    const fromIndex = stopIndexes[stopOffset];
    const toIndex = stopIndexes[stopOffset + 1];
    let durationSeconds = 0;
    let distanceMeters = 0;

    for (let legIndex = fromIndex; legIndex < toIndex; legIndex += 1) {
      const duration = getLegDurationSeconds(legs[legIndex]);
      if (duration == null) {
        throw new Error(
          `Missing travel duration between route points ${legIndex} and ${
            legIndex + 1
          }`,
        );
      }

      durationSeconds += duration;
      distanceMeters += getLegDistanceMeters(legs[legIndex]);
    }

    segments.push({
      fromStopId: coordinateStopIds[fromIndex],
      toStopId: coordinateStopIds[toIndex],
      durationSeconds,
      distanceMeters,
    });
  }

  return segments;
}

export function buildStopScheduleTimeline({
  orderedStops = [],
  startTimeHHmm,
  segmentDurationsSeconds = [],
} = {}) {
  if (!Array.isArray(orderedStops) || orderedStops.length === 0) {
    return {
      stopSchedules: [],
      endTime: startTimeHHmm || "",
      totalMinutes: 0,
    };
  }

  if (!/^\d{2}:\d{2}$/.test(startTimeHHmm || "")) {
    throw new Error("A valid start time (HH:mm) is required.");
  }

  if (orderedStops.length === 1) {
    return {
      stopSchedules: [
        {
          stopId: orderedStops[0].stopId,
          arrivalTime: startTimeHHmm,
          departureTime: startTimeHHmm,
        },
      ],
      endTime: startTimeHHmm,
      totalMinutes: 0,
    };
  }

  if (segmentDurationsSeconds.length !== orderedStops.length - 1) {
    throw new Error(
      "Travel durations are required for every consecutive pair of stops.",
    );
  }

  const stopSchedules = [];
  let currentTime = startTimeHHmm;
  let totalMinutes = 0;

  orderedStops.forEach((stop, index) => {
    if (index === 0) {
      stopSchedules.push({
        stopId: stop.stopId,
        arrivalTime: currentTime,
        departureTime: currentTime,
      });
      return;
    }

    const segmentMinutes = secondsToScheduleMinutes(
      segmentDurationsSeconds[index - 1],
    );

    if (segmentMinutes == null) {
      throw new Error(
        `Invalid travel duration between stop ${index} and stop ${index + 1}.`,
      );
    }

    currentTime = addMinutesToHHmm(currentTime, segmentMinutes);
    totalMinutes += segmentMinutes;

    stopSchedules.push({
      stopId: stop.stopId,
      arrivalTime: currentTime,
      departureTime: currentTime,
    });
  });

  return {
    stopSchedules,
    endTime: currentTime,
    totalMinutes,
  };
}

export function applyTravelDurationsToStopSchedules(
  stops = [],
  startTimeHHmm,
  segmentDurationsSeconds = [],
) {
  const timeline = buildStopScheduleTimeline({
    orderedStops: stops,
    startTimeHHmm,
    segmentDurationsSeconds,
  });

  return {
    ...timeline,
    stopSchedules: stops.map((stop, index) => ({
      ...stop,
      arrivalTime: timeline.stopSchedules[index].arrivalTime,
      departureTime: timeline.stopSchedules[index].departureTime,
    })),
  };
}

export function reverseScheduleStopOrder(
  stops = [],
  segmentDurationsSeconds = [],
  startTime = "",
) {
  const sourceStops = Array.isArray(stops) ? stops : [];
  const sourceDurations = Array.isArray(segmentDurationsSeconds)
    ? segmentDurationsSeconds
    : [];
  const reversedStops = [...sourceStops].reverse();
  const reversedDurations = [...sourceDurations].reverse();
  const normalizedStartTime = /^\d{2}:\d{2}$/.test(startTime || "")
    ? startTime
    : "";

  if (
    normalizedStartTime &&
    canApplyTravelTimes(reversedStops, reversedDurations)
  ) {
    const timeline = applyTravelDurationsToStopSchedules(
      reversedStops,
      normalizedStartTime,
      reversedDurations,
    );
    return {
      stops: timeline.stopSchedules,
      durations: reversedDurations,
      startTime: normalizedStartTime,
      endTime: timeline.endTime,
      travelApplied: true,
    };
  }

  const positionalTimes = sourceStops.map((stop) => ({
    arrivalTime: stop?.arrivalTime || "",
    departureTime: stop?.departureTime || "",
  }));
  const stopsWithTimes = reversedStops.map((stop, index) => {
    const slot = positionalTimes[index] || {};
    const useStartTime = index === 0 && normalizedStartTime;

    return {
      ...stop,
      arrivalTime: useStartTime ? normalizedStartTime : slot.arrivalTime || "",
      departureTime: useStartTime
        ? normalizedStartTime
        : slot.departureTime || "",
    };
  });
  const lastStop = stopsWithTimes[stopsWithTimes.length - 1];

  return {
    stops: stopsWithTimes,
    durations: reversedDurations,
    startTime: normalizedStartTime,
    endTime: lastStop?.arrivalTime || lastStop?.departureTime || "",
    travelApplied: false,
  };
}

function canApplyTravelTimes(stops, durations) {
  return (
    Array.isArray(stops) &&
    stops.length > 1 &&
    Array.isArray(durations) &&
    durations.length === stops.length - 1 &&
    durations.every((duration) => Number.isFinite(Number(duration)) && Number(duration) >= 0)
  );
}

function getStopScheduleTimeMinutes(stopSchedule) {
  const rawTime = stopSchedule?.departureTime || stopSchedule?.arrivalTime || "";
  const timeMatch = String(rawTime).match(/(?:T|^)(\d{2}):(\d{2})/);

  if (!timeMatch) return null;

  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

export function orderStopSchedulesByTimeline(stopSchedules = [], startTime = "") {
  if (!Array.isArray(stopSchedules)) return [];

  const startMinutes = /^\d{2}:\d{2}$/.test(startTime || "")
    ? getStopScheduleTimeMinutes({ departureTime: startTime })
    : null;

  const timelineMinutes = (stopSchedule) => {
    const minutes = getStopScheduleTimeMinutes(stopSchedule);
    if (minutes == null || startMinutes == null || minutes >= startMinutes) {
      return minutes;
    }
    return minutes + 24 * 60;
  };

  return [...stopSchedules].sort((left, right) => {
    const leftMinutes = timelineMinutes(left);
    const rightMinutes = timelineMinutes(right);

    if (
      leftMinutes != null &&
      rightMinutes != null &&
      leftMinutes !== rightMinutes
    ) {
      return leftMinutes - rightMinutes;
    }

    return (Number(left?.id) || 0) - (Number(right?.id) || 0);
  });
}

export function durationsForOrderedStops(orderedStops = [], segments = []) {
  if (!Array.isArray(orderedStops) || orderedStops.length < 2) return [];

  const durationByPair = new Map();
  (Array.isArray(segments) ? segments : []).forEach((segment) => {
    if (segment?.fromStopId == null || segment?.toStopId == null) return;
    durationByPair.set(
      `${segment.fromStopId}:${segment.toStopId}`,
      segment.durationSeconds,
    );
  });

  const durations = [];
  for (let index = 0; index < orderedStops.length - 1; index += 1) {
    const fromStopId = orderedStops[index]?.stopId;
    const toStopId = orderedStops[index + 1]?.stopId;
    const duration =
      durationByPair.get(`${fromStopId}:${toStopId}`) ??
      durationByPair.get(`${toStopId}:${fromStopId}`);
    if (duration == null) return null;
    durations.push(duration);
  }

  return durations;
}

export function getTotalScheduleMinutes(segmentDurationsSeconds = []) {
  return segmentDurationsSeconds.reduce((total, durationSeconds) => {
    const minutes = secondsToScheduleMinutes(durationSeconds);
    return total + (minutes || 0);
  }, 0);
}
